import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { intl } from "LocaleWrapper";

import { FormGroup, Input, Checkbox, showSnackbar } from "@cofano/cck";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import { FormDialog } from "components/dialogs/Dialogs";
import ApplicationLanguageSelect from "components/selects/ApplicationLanguageSelect";

import { AdminLanguage } from "view/admin/utils/admin.types";
import { adminLanguageSchema, AdminLanguageSchemaInput, AdminLanguageSchemaOutput } from "view/admin/utils/admin.schema";

import { SaveLanguageDocument } from "__generated__/view/admin/graphql/admin.mutations.generated";

interface LanguageCreateEditDialogProps {
	open: boolean;
	onClose: () => void;
	language: AdminLanguage | null;
	languages: AdminLanguage[];
	refetch: () => void;
}

const LanguageCreateEditDialog = ({
	open,
	onClose,
	language,
	languages,
	refetch,
}: LanguageCreateEditDialogProps) => {

	const defaultValues = {
		id: null,
		description: "",
		code: "",
		isApplicationLanguage: false,
		linkedLanguage: null,
	};

	const [ saveLanguage, { loading } ] = useMutation(SaveLanguageDocument);

	const { control, handleSubmit, reset } = useForm<AdminLanguageSchemaInput, unknown, AdminLanguageSchemaOutput>({
		resolver: zodResolver(adminLanguageSchema),
		defaultValues: defaultValues,
	});

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const save = (data: AdminLanguageSchemaOutput) => {
		saveLanguage({
			variables: {
				input: {
					id: data.id,
					description: data.description,
					code: data.code,
					isApplicationLanguage: data.isApplicationLanguage,
					linkedLanguage: data.linkedLanguage,
				},
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
			onClose();
			refetch();
		});
	};
	useEffect(() =>	 {
		if (open && language) {
			reset({
				id: language.id,
				code: language.code,
				description: language.description,
				isApplicationLanguage: language.isApplicationLanguage,
				linkedLanguage: language.linkedLanguage,
			});
		} else if (open) {
			reset({
				id: null,
				description: "",
				code: "",
				isApplicationLanguage: false,
				linkedLanguage: languages[0] ?? null,
			});
		}
	}, [ open, language, languages ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "USER_LANGUAGE" })}
			form='languageForm'
			loading={loading}>
			<form onSubmit={handleSubmit((form) => save(form))} id='languageForm'>
				<FormGroup label={intl.formatMessage({ id: "DESCRIPTION" })} labelPlacement='left-center' required>
					<Controller
						name='description'
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "CODE" })} labelPlacement='left-center' required>
					<Controller
						name='code'
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "APPLICATION_LANGUAGE" })} labelPlacement='left-center'>
					<Controller
						name='isApplicationLanguage'
						control={control}
						render={({ field }) => <Checkbox checked={field.value} onChange={event => field.onChange(event.target.checked)} />} />
				</FormGroup>

				<FormGroup
					label={intl.formatMessage({ id: "LINKED_LANGUAGE" })}
					labelPlacement='left-center'
					required>
					<Controller
						name='linkedLanguage'
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<ApplicationLanguageSelect {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default LanguageCreateEditDialog;
